/**
 * ステージ解放判定(docs/02_game_design.md §6.4)。
 *
 * 「直前ステージ★1以上で次を解放。複合復習は構成ステージ全クリアで解放」。
 * 星は難易度別に記録されるが、解放は**難易度をまたいだ最高星数**で判定する
 * (かんたんでクリアした子がふつうに切り替えた瞬間に全部ロックされるのを防ぐため)。
 */

import { TYPING_STAGE_RULES, type Difficulty } from '@paltyping/engine';
import type { StageDefinition } from '../services/content';
import { languageOption, type LanguageCode } from '../services/languages';
import { isDebugUnlockAll } from '../services/debugUnlock';
import { bestStarsOf, type ProgressSnapshot } from '../services/progress';

function graduationProgress(
  progress: ProgressSnapshot,
  stageId: string,
): ProgressSnapshot['stages'][string] | undefined {
  return progress.stages[stageId];
}

/** 卒業済みは現在の母語設定やデバッグ解放より常に優先する。 */
export function isStageGraduated(
  stageId: string,
  difficulty: Difficulty,
  progress: ProgressSnapshot,
): boolean {
  return graduationProgress(progress, stageId)?.graduated?.[difficulty] === true;
}

/** 母語ステージならば、今回の難易度に記録した★5クリア回数を返す。 */
export function nativeFiveStarClears(
  stage: StageDefinition,
  difficulty: Difficulty,
  firstLanguage: LanguageCode,
  progress: ProgressSnapshot,
): number | null {
  if (languageOption(firstLanguage)?.mode !== stage.language) return null;
  return graduationProgress(progress, stage.id)?.nativeFiveStarClears?.[difficulty] ?? 0;
}

/** 母語ステージの卒業までに必要な★5クリアの残り回数。 */
export function nativeFiveStarClearsRemaining(
  stage: StageDefinition,
  difficulty: Difficulty,
  firstLanguage: LanguageCode,
  progress: ProgressSnapshot,
): number | null {
  const clears = nativeFiveStarClears(stage, difficulty, firstLanguage, progress);
  return clears === null
    ? null
    : Math.max(0, TYPING_STAGE_RULES.nativeFiveStarClearsToGraduate - clears);
}

/**
 * ステージが解放されているか。
 *
 * デバッグフラグ(`services/debugUnlock.ts`。`?debugUnlock=1`)が有効なときは常に true。
 * 進捗データは書き換えないので、フラグを消せば元の解放状態に戻る。
 */
export function isStageUnlocked(stage: StageDefinition, progress: ProgressSnapshot): boolean {
  if (isDebugUnlockAll()) return true;
  // 手動解除は難易度をまたいでステージ単位に保存される(docs/02 §6.4.1)。
  // 星や前提の記録は付け替えないので、通常の判定へ OR で足すだけにする。
  if (progress.stages[stage.id]?.manualUnlocked === true) return true;
  const unlock = stage.unlock;
  if (!unlock || unlock.requires.length === 0) return true;
  const minStars = Math.max(1, unlock.minStars);
  return unlock.requires.every((requiredId) => bestStarsOf(progress, requiredId) >= minStars);
}

/** 一覧表示用に解放状態を添えたステージ。 */
export interface StageListItem {
  readonly stage: StageDefinition;
  readonly unlocked: boolean;
  /** 選択中難易度の星。 */
  readonly stars: number;
  readonly bestScore: number;
}

/** セクション見出しでグルーピングした一覧。 */
export interface StageSection {
  readonly section: string;
  readonly items: readonly StageListItem[];
}

/** カテゴリで絞り、セクション順を保ったままグルーピングする。 */
export function groupBySection(items: readonly StageListItem[]): readonly StageSection[] {
  const order: string[] = [];
  const buckets = new Map<string, StageListItem[]>();
  for (const item of items) {
    const key = item.stage.section;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      order.push(key);
    }
    bucket.push(item);
  }
  return order.map((section) => ({ section, items: buckets.get(section) ?? [] }));
}
